import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabase/client';

// Operator-facing management of dripify_campaign_mappings for one STEC campaign. This is the
// ONLY UI surface that writes to this table -- it is the canonical routing registry (see
// STEC generic-routing Phases A-D). campaigns.dripify_names[] (managed separately by
// AdminDripifyEntry.tsx) is a different, reporting-only mechanism and is never touched here.

interface AliasOption {
  id: string;
  team_member_name: string;
}

interface Mapping {
  id: string;
  dripify_campaign_name: string;
  sending_alias_id: string;
  automation_enabled: boolean;
  created_at: string;
  alias_name?: string;
}

interface Props {
  campaignId: string;
}

const DripifyMappingsPanel: React.FC<Props> = ({ campaignId }) => {
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [aliases, setAliases] = useState<AliasOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Add-mapping form state
  const [newName, setNewName] = useState('');
  const [newAliasId, setNewAliasId] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const loadAliases = useCallback(async () => {
    try {
      const { data, error: fnError } = await supabase.functions.invoke('outreach-team-config', {
        body: { action: 'list' },
      });
      if (fnError) throw fnError;
      const active: AliasOption[] = (data?.data ?? data ?? [])
        .filter((m: any) => m.is_active !== false)
        .map((m: any) => ({ id: m.id, team_member_name: m.team_member_name }));
      setAliases(active);
    } catch (e) {
      console.error('Failed to load outreach aliases:', e);
    }
  }, []);

  const loadMappings = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from('dripify_campaign_mappings')
      .select('id, dripify_campaign_name, sending_alias_id, automation_enabled, created_at')
      .eq('campaign_id', campaignId)
      .order('created_at', { ascending: true });

    if (fetchError) {
      setError('Failed to load Dripify mappings.');
      setLoading(false);
      return;
    }

    const rows = data ?? [];
    const aliasIds = [...new Set(rows.map((r) => r.sending_alias_id))];
    let aliasNameById: Record<string, string> = {};
    if (aliasIds.length > 0) {
      const { data: aliasRows } = await supabase
        .from('outreach_team_config')
        .select('id, team_member_name')
        .in('id', aliasIds);
      (aliasRows ?? []).forEach((a: any) => { aliasNameById[a.id] = a.team_member_name; });
    }

    setMappings(rows.map((r) => ({ ...r, alias_name: aliasNameById[r.sending_alias_id] ?? 'Unknown alias' })));
    setLoading(false);
  }, [campaignId]);

  useEffect(() => {
    loadAliases();
    loadMappings();
  }, [loadAliases, loadMappings]);

  const handleAdd = async () => {
    setAddError(null);
    const trimmedName = newName.trim();
    if (!trimmedName) {
      setAddError('Enter the exact Dripify campaign name.');
      return;
    }
    if (!newAliasId) {
      setAddError('Select a sending alias.');
      return;
    }

    setAdding(true);
    const { error: insertError } = await supabase
      .from('dripify_campaign_mappings')
      .insert({
        dripify_campaign_name: trimmedName,
        campaign_id: campaignId,
        sending_alias_id: newAliasId,
        automation_enabled: false, // always starts disabled -- explicit enable is a separate, deliberate step
      });

    if (insertError) {
      if (insertError.code === '23505') {
        // Look up which campaign already owns this exact name for a genuinely useful message.
        const { data: existing } = await supabase
          .from('dripify_campaign_mappings')
          .select('campaign_id, campaigns(name)')
          .eq('dripify_campaign_name', trimmedName)
          .maybeSingle();
        const ownerName = (existing as any)?.campaigns?.name;
        setAddError(
          ownerName
            ? `"${trimmedName}" is already registered to campaign "${ownerName}". Each Dripify campaign name can only be mapped once, system-wide.`
            : `"${trimmedName}" is already registered to another STEC campaign. Each Dripify campaign name can only be mapped once, system-wide.`
        );
      } else {
        setAddError('Failed to add mapping. You may not be authorized to manage this campaign.');
      }
      setAdding(false);
      return;
    }

    setNewName('');
    setNewAliasId('');
    setAdding(false);
    loadMappings();
  };

  const handleToggleAutomation = async (mapping: Mapping) => {
    if (!mapping.automation_enabled) {
      const confirmed = window.confirm(
        `Enable automation for "${mapping.dripify_campaign_name}"?\n\n` +
        `Once enabled:\n` +
        `• Future Dripify replies for this exact campaign name will be turned into Lead Responses automatically.\n` +
        `• Manual "Add a Lead Response" will be blocked for this STEC campaign while this (or any) mapping stays active.\n\n` +
        `If something goes wrong after enabling, disable it here rather than trying to log replies manually.`
      );
      if (!confirmed) return;
    }

    setBusyId(mapping.id);
    const { error: updateError } = await supabase
      .from('dripify_campaign_mappings')
      .update({ automation_enabled: !mapping.automation_enabled })
      .eq('id', mapping.id);

    if (updateError) {
      alert('Failed to update automation status. You may not be authorized to manage this campaign.');
    } else {
      await loadMappings();
    }
    setBusyId(null);
  };

  const handleRemove = async (mapping: Mapping) => {
    const confirmed = window.confirm(
      `Remove the mapping for "${mapping.dripify_campaign_name}"?\n\n` +
      `This cannot be undone. If this mapping has already processed real replies, removal will be refused ` +
      `to protect that history -- disable it instead in that case.`
    );
    if (!confirmed) return;

    setBusyId(mapping.id);
    const { error: deleteError } = await supabase
      .from('dripify_campaign_mappings')
      .delete()
      .eq('id', mapping.id);

    if (deleteError) {
      if (deleteError.code === '23503') {
        alert(
          `"${mapping.dripify_campaign_name}" has already processed real replies and can't be deleted -- ` +
          `that history has to be preserved. Disable automation instead if you need to stop routing.`
        );
      } else {
        alert('Failed to remove mapping. You may not be authorized to manage this campaign.');
      }
    } else {
      await loadMappings();
    }
    setBusyId(null);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl font-bold text-gray-900">Dripify Response Automation</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Map external Dripify campaign names to this STEC campaign so inbound LinkedIn replies are turned into Lead Responses automatically.
        No experiment setup required.
      </p>

      {loading ? (
        <div className="text-sm text-gray-500 py-4">Loading mappings…</div>
      ) : error ? (
        <div className="text-sm text-red-600 py-4">{error}</div>
      ) : (
        <>
          {mappings.length === 0 ? (
            <div className="text-sm text-gray-500 mb-4 py-3 border border-dashed border-gray-200 rounded-lg text-center">
              No Dripify campaigns mapped yet. Replies for this campaign must be logged manually until one is added and enabled.
            </div>
          ) : (
            <table className="w-full text-sm mb-4">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 font-medium text-gray-700">Dripify Campaign Name</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Sending Alias</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Status</th>
                  <th className="text-right py-2 pl-3 font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((m) => (
                  <tr key={m.id} className="border-b border-gray-100">
                    <td className="py-2 pr-4 font-mono text-xs text-gray-800">{m.dripify_campaign_name}</td>
                    <td className="py-2 px-3 text-gray-800">{m.alias_name}</td>
                    <td className="py-2 px-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                          m.automation_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {m.automation_enabled ? 'AUTOMATION ON' : 'Disabled'}
                      </span>
                    </td>
                    <td className="py-2 pl-3 text-right space-x-2">
                      <button
                        onClick={() => handleToggleAutomation(m)}
                        disabled={busyId === m.id}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50 ${
                          m.automation_enabled
                            ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                            : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                      >
                        {m.automation_enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        onClick={() => handleRemove(m)}
                        disabled={busyId === m.id}
                        className="px-3 py-1 rounded text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100 transition-colors disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Add mapping */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Add a Dripify campaign</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Exact Dripify campaign name"
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <select
                value={newAliasId}
                onChange={(e) => setNewAliasId(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select sending alias…</option>
                {aliases.map((a) => (
                  <option key={a.id} value={a.id}>{a.team_member_name}</option>
                ))}
              </select>
              <button
                onClick={handleAdd}
                disabled={adding}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {adding ? 'Adding…' : 'Add mapping (starts disabled)'}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Must match the Dripify campaign name exactly — no fuzzy matching is used for routing. A new mapping always
              starts disabled; enable it only after confirming the name is correct.
            </p>
            {addError && <p className="text-xs text-red-600 mt-2">{addError}</p>}
          </div>
        </>
      )}
    </div>
  );
};

export default DripifyMappingsPanel;
